import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Target } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import ServiceIcon from "../components/ServiceIcon";
import { SERVICES } from "../lib/data";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <>
      <Seo
        title={service.seoTitle || `${service.title} Physiotherapy`}
        description={service.seoDescription || `${service.title} treatment in Sandringham. ${service.short} Conditions treated, expected outcomes and FAQs. Book online at Arena Movement Physiotherapy.`}
        path={`/services/${slug}`}
      />

      {/* HERO */}
      <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-20" data-testid="service-hero">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-navy/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium" data-testid="back-to-services">
            <ArrowLeft className="h-4 w-4" /> All services
          </Link>
          <div className="mt-6 h-14 w-14 grid place-items-center rounded-2xl bg-white/15 backdrop-blur text-white border border-white/20">
            <ServiceIcon name={service.icon} className="h-7 w-7" />
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter max-w-3xl">{service.title}</h1>
          <p className="mt-5 text-lg text-white/85 max-w-2xl leading-relaxed">{service.short}</p>
          <Link to="/book" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-semibold text-navy transition-all hover:-translate-y-0.5" data-testid="service-book-button">
            Book Appointment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* OVERVIEW + LISTS */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-3 gap-12">
          <Reveal className="lg:col-span-2">
            <div>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-navy">Overview</span>
              <h2 className="mt-4 font-display text-3xl font-semibold text-text-dark tracking-tight">How we help</h2>
              <p className="mt-5 text-lg text-text-muted leading-relaxed">{service.overview}</p>

              <h3 className="mt-12 font-display text-xl font-semibold text-text-dark">Conditions we treat</h3>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {service.conditions.map((c) => (
                  <div key={c} className="flex items-center gap-3 rounded-2xl bg-offwhite border border-border-light px-4 py-3">
                    <CheckCircle2 className="h-5 w-5 text-navy shrink-0" /> <span className="text-text-dark">{c}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-navy p-8 text-white sticky top-28">
              <Target className="h-7 w-7 text-emerald-300" />
              <h3 className="mt-4 font-display text-xl font-semibold">Expected outcomes</h3>
              <ul className="mt-5 space-y-3">
                {service.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-3 text-white/85">
                    <CheckCircle2 className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" /> {o}
                  </li>
                ))}
              </ul>
              <Link to="/book" className="mt-7 inline-flex w-full justify-center items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-navy hover:-translate-y-0.5 transition-all" data-testid="service-sidebar-book">
                Start your recovery
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 bg-offwhite" data-testid="service-faq">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-navy">FAQs</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-text-dark tracking-tight">Frequently asked questions</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-8">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border-light" data-testid={`service-faq-${i}`}>
                  <AccordionTrigger className="text-left font-display text-lg font-medium text-text-dark hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-text-muted leading-relaxed text-base">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* NEXT SERVICE */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Link to={`/services/${next.slug}`} className="group flex items-center justify-between rounded-3xl border border-border-light bg-white p-7 card-hover" data-testid="next-service">
            <div>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-navy/60">Next service</span>
              <div className="mt-1 font-display text-xl font-semibold text-text-dark">{next.title}</div>
            </div>
            <ArrowRight className="h-6 w-6 text-navy group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </>
  );
}
